import { useState } from "react";
import { InfoCircleFill, KeyFill, LightningChargeFill, QrCode, Pencil } from "react-bootstrap-icons";
import { IntegrationStatus } from "./Data/ApiTransport";
import { ServerRequestsParkerIntegrationPartyDto } from "./shared/api/axios-client";
import { Button } from "./Controls/Button";
import { EditRfidModal } from "./Controls/EditRfidModal";
import { supportsCredentialUpdate, suppressesQr } from "./Data/IntegrationCapabilities";
import { getIntegrationDisplayName, getIntegrationDescription } from "./Data/IntegrationDisplay";

interface Props {
  integrations: ServerRequestsParkerIntegrationPartyDto[];
  subscriptionId: number;
  facilityId: number;
  setPendingToggle: (data: {
    integrationPartyId: number;
    subscriptionId: number;
    parkerId: number;
    facilityId: number;
    newStatus: IntegrationStatus;
  }) => void;
  setShowToggleConfirm: (show: boolean) => void;
  setQrCodeData: (data: string) => void;
  setShowQrModal: (show: boolean) => void;
  setParkerIntegrations?: React.Dispatch<
    React.SetStateAction<ServerRequestsParkerIntegrationPartyDto[]>
  >;
  hasSubParkers?: boolean;
  hasEnrolledIntegrations?: boolean;
}

export function ParkerIntegrations({
  integrations,
  subscriptionId,
  facilityId,
  setPendingToggle,
  setShowToggleConfirm,
  setQrCodeData,
  setShowQrModal,
  setParkerIntegrations,
  hasSubParkers = false,
  hasEnrolledIntegrations = false,
}: Props) {
  const showManagedViaParkersMessage =
    hasSubParkers && !hasEnrolledIntegrations && integrations.length > 0;
  const [editRfidIntegration, setEditRfidIntegration] =
    useState<ServerRequestsParkerIntegrationPartyDto | null>(null);

  const canEdit = (integration: ServerRequestsParkerIntegrationPartyDto) =>
    supportsCredentialUpdate(integration) &&
    integration.isActive &&
    setParkerIntegrations;

  const handleRfidUpdated = (updated: ServerRequestsParkerIntegrationPartyDto) => {
    if (setParkerIntegrations) {
      setParkerIntegrations((prev) => {
        const keyMatch = (i: ServerRequestsParkerIntegrationPartyDto) =>
          i.integrationPartyId === updated.integrationPartyId &&
          i.parkerId === updated.parkerId &&
          (i.vehicleId ?? undefined) === (updated.vehicleId ?? undefined);
        return prev.some(keyMatch)
          ? prev.map((i) => (keyMatch(i) ? { ...i, ...updated } : i))
          : [...prev, updated];
      });
    }
  };

  return (
    <div className="border rounded mb-4 overflow-hidden">
      <div
        className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
        style={{ backgroundColor: "color-mix(in srgb, var(--color-light) 35%, white)" }}
      >
        <div
          className="rounded d-flex align-items-center justify-content-center flex-shrink-0 text-white"
          style={{ width: 32, height: 32, backgroundColor: "var(--color-mid)" }}
        >
          <KeyFill />
        </div>
        <strong style={{ color: "var(--color-very-dark)" }}>Access Passes</strong>
      </div>

      <div className="p-3 d-flex flex-column gap-3">
        {showManagedViaParkersMessage ? (
          <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0">
            <InfoCircleFill color="warning" className="mt-1 flex-shrink-0" />
            <div className="small">
              Access passes are managed via individual parkers below. 
            </div>
          </div>
        ) : integrations.length > 0 ? (
          integrations.map((integration) => {
            const effectiveActive =
              integration.isActive &&
              !(suppressesQr(integration) && !integration.metaFlashRFIDNumber);
            return (
            <div
              key={`${integration.integrationPartyId}-${integration.parkerId}`}
              className="border rounded px-3 py-2 bg-white access-pass-card"
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 gap-md-3">
                <div className="d-flex flex-column gap-1">
                  <div className="d-flex align-items-center flex-wrap gap-2 gap-md-3">
                    <LightningChargeFill
                      className="flex-shrink-0"
                      style={{ color: "var(--color-mid)" }}
                    />
                    <span
                      className="fw-bold"
                      style={{ color: "var(--color-very-dark)" }}
                    >
                      {getIntegrationDisplayName(integration.integrationName)}
                    </span>
                    <span className="text-muted small">
                      {integration.metaFlashRFIDNumber
                        ? `RFID ${integration.metaFlashRFIDNumber}`
                        : "RFID —"}
                    </span>
                    {canEdit(integration) && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 border-0 text-decoration-none d-inline-flex align-items-center"
                        style={{ color: "var(--color-mid)" }}
                        onClick={() => setEditRfidIntegration(integration)}
                      >
                        <Pencil size={12} className="me-1 icon-shift-up" />
                        Edit
                      </button>
                    )}
                  </div>
                  {getIntegrationDescription(integration.integrationName) && (
                    <span className="text-muted small ps-1" style={{ fontSize: "0.75rem", opacity: 0.9 }}>
                      {getIntegrationDescription(integration.integrationName)}
                    </span>
                  )}
                </div>

                <div className="d-flex align-items-center gap-3 flex-shrink-0">
                  <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
                    <input
                      className="form-check-input m-0"
                      type="checkbox"
                      role="switch"
                      id={`switch-${integration.integrationPartyId}-${integration.parkerId}`}
                      checked={effectiveActive}
                      onChange={() => {
                        const newStatus = integration.isActive
                          ? IntegrationStatus.Deactivated
                          : IntegrationStatus.Activated;

                        setPendingToggle({
                          integrationPartyId: integration.integrationPartyId!,
                          subscriptionId,
                          parkerId: integration.parkerId!,
                          facilityId,
                          newStatus,
                        });
                        setShowToggleConfirm(true);
                      }}
                    />
                    <label
                      className="form-check-label text-muted small mb-0"
                      htmlFor={`switch-${integration.integrationPartyId}-${integration.parkerId}`}
                    >
                      {effectiveActive ? "Active" : "Inactive"}
                    </label>
                  </div>

                  {(integration.isActive || integration.parkerId === 0) &&
                    integration.metaFlashRFIDNumber &&
                    !suppressesQr(integration) && (
                      <Button
                        className="rounded-pill py-1 px-3 bg-white text-dark border-2"
                        style={{ borderColor: "var(--color-mid)" }}
                        onClick={() => {
                          setQrCodeData(integration.metaFlashRFIDNumber!);
                          setShowQrModal(true);
                        }}
                      >
                        <QrCode
                          className="me-1"
                          style={{ marginTop: "-2px" }}
                        />
                        QR code
                      </Button>
                    )}
                </div>
              </div>
            </div>
            );
          })
        ) : (
          <div className="text-muted small mb-2">No active integrations.</div>
        )}
      </div>

      {editRfidIntegration && (
        <EditRfidModal
          show={!!editRfidIntegration}
          setShow={(show) => !show && setEditRfidIntegration(null)}
          integration={editRfidIntegration}
          subscriptionId={subscriptionId}
          parkerId={editRfidIntegration.parkerId!}
          facilityId={facilityId}
          onSuccess={(updated) => {
            handleRfidUpdated(updated);
            setEditRfidIntegration(null);
          }}
        />
      )}
    </div>
  );
}